/**
 * VoiceSessionCoordinator
 *
 * Manages the full voice pipeline on the client:
 *   - Mic capture via AudioWorklet (16kHz mono PCM16)
 *   - WebSocket lifecycle (connect with ticket, reconnect with backoff)
 *   - Playback via AudioWorklet (24kHz ring buffer)
 *   - FSM mirror tracking voiceState from server state messages
 *
 * Usage:
 *   const coordinator = new VoiceSessionCoordinator({ apiUrl, getToken, onState, onMessage, onError });
 *   await coordinator.connect(chatSessionId, mode, provider);
 *   coordinator.startRecording();
 *   coordinator.stopRecording();
 *   coordinator.stopPlayback();
 *   coordinator.destroy();
 */

const CAPTURE_SAMPLE_RATE = 16000;
const PLAYBACK_SAMPLE_RATE = 24000;
const MAX_BUFFERED_AMOUNT = 65536; // 64KB backpressure threshold
const MAX_RECONNECT_RETRIES = 5;

export class VoiceSessionCoordinator {
  /** @type {'idle' | 'connecting' | 'wait' | 'query' | 'processing' | 'playback' | 'error'} */
  voiceState = 'idle';

  constructor({ apiUrl, getToken, onState, onMessage, onMetrics, onSessionInit, onError }) {
    this._apiUrl = apiUrl;
    this._getToken = getToken;
    this._onState = onState;
    this._onMessage = onMessage;
    this._onMetrics = onMetrics;
    this._onSessionInit = onSessionInit;
    this._onError = onError;

    this._ws = null;
    this._captureCtx = null;
    this._captureNode = null;
    this._mediaStream = null;
    this._playbackCtx = null;
    this._playbackNode = null;
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._destroyed = false;
    this._muted = false;
    this._chatSessionId = null;
    this._mode = null;
  }

  get isMuted() { return this._muted; }

  // ── Public API ──────────────────────────────────────────

  async connect(chatSessionId, mode, provider) {
    if (this._destroyed) return;
    this._chatSessionId = chatSessionId;
    this._mode = mode;
    this._provider = provider;
    this._setState('connecting');

    try {
      // Get mic permission early so we fail fast
      this._mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: CAPTURE_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Initialize capture AudioContext + worklet
      this._captureCtx = new AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE });
      await this._captureCtx.audioWorklet.addModule('/audio-capture-processor.js');

      // Initialize playback AudioContext + worklet
      this._playbackCtx = new AudioContext({ sampleRate: PLAYBACK_SAMPLE_RATE });
      await this._playbackCtx.audioWorklet.addModule('/pcm-playback-processor.js');

      this._playbackNode = new AudioWorkletNode(this._playbackCtx, 'pcm-playback-processor');
      this._playbackNode.port.onmessage = (e) => {
        if (e.data === 'drain' && this.voiceState === 'playback') {
          // Playback finished naturally — server already sent resp_stop
        }
      };
      this._playbackNode.connect(this._playbackCtx.destination);

      // Connect WebSocket
      await this._connectWs();
      this._setState('wait');
    } catch (err) {
      this._handleError(err.name === 'NotAllowedError' ? 'MIC_PERMISSION_DENIED' : 'CONNECTION_FAILED', err.message);
    }
  }

  mute() {
    this._muted = true;

    // If actively recording, tear down capture and notify server
    if (this.voiceState === 'query') {
      if (this._captureNode) {
        this._captureNode.port.postMessage('stop');
        this._captureNode.disconnect();
        this._captureNode = null;
      }
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ type: 'aq_stop' }));
      }
    }
  }

  unmute() {
    this._muted = false;
  }

  startRecording() {
    if (this._muted || this.voiceState !== 'wait' || !this._ws || this._ws.readyState !== WebSocket.OPEN) return;

    // Send aq_start
    this._ws.send(JSON.stringify({
      type: 'aq_start',
      chatSessionId: this._chatSessionId,
      mode: this._mode,
    }));

    // Wire up capture
    if (this._captureCtx && this._mediaStream) {
      const source = this._captureCtx.createMediaStreamSource(this._mediaStream);
      this._captureNode = new AudioWorkletNode(this._captureCtx, 'audio-capture-processor');
      this._captureNode.port.onmessage = (e) => {
        if (this._muted) return; // drop frames silently when muted
        if (e.data instanceof ArrayBuffer && this._ws && this._ws.readyState === WebSocket.OPEN) {
          // Backpressure check
          if (this._ws.bufferedAmount < MAX_BUFFERED_AMOUNT) {
            this._ws.send(e.data);
          }
        }
      };
      source.connect(this._captureNode);
      this._captureNode.connect(this._captureCtx.destination); // needed for process() to run
    }
  }

  stopRecording() {
    if (this.voiceState !== 'query') return;

    // Stop capture worklet
    if (this._captureNode) {
      this._captureNode.port.postMessage('stop');
      this._captureNode.disconnect();
      this._captureNode = null;
    }

    // Send aq_stop
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({ type: 'aq_stop' }));
    }
  }

  setMode(mode) {
    this._mode = mode;
  }

  stopPlayback() {
    if (this.voiceState !== 'playback') return;

    // Clear playback buffer
    if (this._playbackNode) {
      this._playbackNode.port.postMessage('clear');
    }

    // Send play_stop (barge-in)
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({ type: 'play_stop' }));
    }
  }

  destroy() {
    this._destroyed = true;
    this._muted = false;
    clearTimeout(this._reconnectTimer);

    if (this._captureNode) {
      this._captureNode.port.postMessage('stop');
      this._captureNode.disconnect();
    }
    if (this._playbackNode) {
      this._playbackNode.disconnect();
    }
    if (this._captureCtx) {
      this._captureCtx.close().catch(() => {});
    }
    if (this._playbackCtx) {
      this._playbackCtx.close().catch(() => {});
    }
    if (this._mediaStream) {
      this._mediaStream.getTracks().forEach(t => t.stop());
    }
    if (this._ws) {
      this._ws.close(1000, 'destroyed');
    }

    this._setState('idle');
  }

  // ── WebSocket ─────────────────────────────────────────

  async _connectWs() {
    const token = await this._getToken();
    if (!token) {
      this._handleError('AUTH_ERROR', 'No auth token');
      return;
    }

    // Get ticket
    const res = await fetch(`${this._apiUrl}/api/voice/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        chatSessionId: this._chatSessionId,
        mode: this._mode,
        provider: this._provider,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to get voice ticket: ${res.status}`);
    }

    const { ticket } = await res.json();

    // Build WS URL
    const wsProtocol = this._apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = this._apiUrl.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/voice/ws?ticket=${ticket}`;

    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl);
      ws.binaryType = 'arraybuffer';

      ws.onopen = () => {
        this._ws = ws;
        this._reconnectAttempts = 0;
        resolve();
      };

      ws.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          // Binary: PCM audio from server → playback
          if (this._playbackNode) {
            this._playbackNode.port.postMessage(event.data, [event.data]);
          }
        } else {
          // Text: JSON control message
          try {
            const msg = JSON.parse(event.data);
            this._handleServerMessage(msg);
          } catch { /* ignore */ }
        }
      };

      ws.onclose = (event) => {
        if (this._destroyed) return;
        if (event.code !== 1000) {
          this._attemptReconnect();
        } else {
          this._setState('idle');
        }
      };

      ws.onerror = () => {
        reject(new Error('WebSocket connection failed'));
      };
    });
  }

  _handleServerMessage(msg) {
    switch (msg.type) {
      case 'session_init':
        this._chatSessionId = msg.sessionId;
        this._onSessionInit?.(msg.sessionId);
        break;

      case 'state':
        this._setState(msg.state);
        break;

      case 'resp_start':
        this._onMessage?.({ type: 'resp_start', userText: msg.userText });
        break;

      case 'output_text':
        this._onMessage?.({ type: 'output_text', delta: msg.delta, cumulativeChars: msg.cumulativeChars });
        break;

      case 'resp_stop':
        this._onMessage?.({ type: 'resp_stop' });
        break;

      case 'metrics':
        this._onMetrics?.(msg);
        break;

      case 'error':
        this._handleError(msg.code, msg.message);
        break;
    }
  }

  _attemptReconnect() {
    if (this._destroyed || this._reconnectAttempts >= MAX_RECONNECT_RETRIES) {
      this._handleError('CONNECTION_LOST', 'Voice connection lost');
      return;
    }

    this._reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempts - 1), 30000);
    this._setState('connecting');

    this._reconnectTimer = setTimeout(async () => {
      try {
        await this._connectWs();
      } catch {
        this._attemptReconnect();
      }
    }, delay);
  }

  // ── State ─────────────────────────────────────────────

  _setState(state) {
    if (this.voiceState === state) return;
    const prev = this.voiceState;
    this.voiceState = state;

    // Stop capture when leaving query state (server detected end of speech)
    if (prev === 'query' && state !== 'query') {
      if (this._captureNode) {
        this._captureNode.port.postMessage('stop');
        this._captureNode.disconnect();
        this._captureNode = null;
      }
    }

    this._onState?.(state);
  }

  _handleError(code, message) {
    this._setState('error');
    this._onError?.(code, message);
  }
}
